import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { OrgConsumerNoticesSection } from '@/components/public/org/OrgConsumerNoticesSection';
import { OrgConsumerSidebar } from '@/components/public/org/OrgConsumerSidebar';
import { BookingWeeklySchedule } from '@/components/schedule/BookingWeeklySchedule';
import { TermLessonCalendar } from '@/components/schedule/TermLessonCalendar';
import {
  ScheduleViewHeaderToggle,
  type ScheduleCalendarView,
} from '@/components/schedule/ScheduleViewHeaderToggle';
import { LessonDetailModal } from '@/components/bookings/LessonDetailModal';
import { RefundRequestModal } from '@/components/bookings/RefundRequestModal';
import { ReportAbsenceFromCalendar } from '@/components/schedule/ReportAbsenceFromCalendar';
import { CourtHireBookingDetailPopover } from '@/components/member/CourtHireBookingDetailPopover';
import { useOrgConsumerSchedule } from '@/hooks/useOrgConsumerSchedule';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useOrgMemberStore } from '@/store/orgMember';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useGuardianPaths } from '@/hooks/useGuardianPaths';
import { useRefundRequest } from '@/hooks/useRefundRequest';
import { bookingService } from '@/services/booking';
import { publicClubMembershipService } from '@/services/publicClubMembership';
import type { Booking } from '@court-sync/shared';
import type { ScheduleOccurrence } from '@/hooks/useSchedule';
import type { ConsumerCalendarExtraEvent } from '@/utils/consumerCalendarEvents';
import toast from 'react-hot-toast';

export function OrgConsumerHome() {
  const consumerNav = useUnifiedConsumerNav();
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);
  const academy = useGuardianAcademyStore((s) => s.academy);
  const { p } = useOrgPaths();
  const paths = useGuardianPaths();
  const { requestRefund } = useRefundRequest();

  const {
    hasLessonsAccess,
    hasClubMemberAccess,
    hasMemberships,
    hasCourtHire,
  } = consumerNav;

  const {
    lessonOccurrences,
    makeupOccurrenceSet,
    scheduleData,
    courtBookings,
    courtExtraEvents,
    studentsById,
    isLoading,
    refetchSchedule,
  } = useOrgConsumerSchedule({
    hasLessonsAccess,
    hasCourtHire,
    hasInventoryHire: consumerNav.hasInventoryHire,
    academyId: academy?.id,
    clubSlug,
  });

  const membershipQuery = useQuery({
    queryKey: ['org-consumer-membership', clubSlug],
    enabled: hasMemberships && Boolean(clubSlug),
    queryFn: async () => {
      const res = await publicClubMembershipService.getMyMembership(clubSlug!);
      return res.success ? res.data : null;
    },
  });

  const [scheduleView, setScheduleView] = useState<ScheduleCalendarView>('weekly');
  const [detailOccurrence, setDetailOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [absenceOccurrence, setAbsenceOccurrence] = useState<ScheduleOccurrence | null>(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [refundBooking, setRefundBooking] = useState<Booking | null>(null);
  const [selectedCourtEvent, setSelectedCourtEvent] = useState<ConsumerCalendarExtraEvent | null>(null);

  const bookingsQuery = useQuery({
    queryKey: ['org-consumer-bookings', academy?.id],
    enabled: hasLessonsAccess && Boolean(academy?.id),
    queryFn: async () => {
      const response = await bookingService.getMyBookings(academy!.id);
      return response.data || [];
    },
  });

  const bookings = bookingsQuery.data ?? [];

  const termStart = useMemo(() => {
    const starts: number[] = [];
    if (scheduleData?.terms) {
      Object.values(scheduleData.terms).forEach((term) =>
        starts.push(new Date(term.startDate).getTime())
      );
    }
    return starts.length > 0 ? new Date(Math.min(...starts)) : undefined;
  }, [scheduleData]);

  const termEnd = useMemo(() => {
    const ends: number[] = [];
    if (scheduleData?.terms) {
      Object.values(scheduleData.terms).forEach((term) =>
        ends.push(new Date(term.endDate).getTime())
      );
    }
    return ends.length > 0 ? new Date(Math.max(...ends)) : undefined;
  }, [scheduleData]);

  const courtBookingsPath = p('/court-hire/bookings');

  const handleOccurrenceClick = (occ: ScheduleOccurrence) => {
    setDetailOccurrence(occ);
    setReportSuccess(false);
  };

  const handleReportAbsence = (occ: ScheduleOccurrence) => {
    setAbsenceOccurrence(occ);
    setDetailOccurrence(null);
  };

  const handleAbsenceSuccess = () => {
    const occ = absenceOccurrence;
    setAbsenceOccurrence(null);
    setReportSuccess(true);
    if (occ) setDetailOccurrence(occ);
    void refetchSchedule();
    toast.success('Absence reported. You earned a make-up credit!');
  };

  const handleRequestRefund = (occ: ScheduleOccurrence) => {
    const booking = bookings.find((b) => b.id === occ.bookingId);
    if (booking) setRefundBooking(booking);
  };

  const handleRefundConfirm = async (reason?: string) => {
    if (!refundBooking) return;
    await requestRefund(refundBooking.id, reason);
    setRefundBooking(null);
    setDetailOccurrence(null);
    void bookingsQuery.refetch();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid gap-8 lg:grid-cols-[1fr_20rem] xl:grid-cols-[1fr_22rem]">
        <div className="min-w-0 space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">My schedule</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {hasLessonsAccess && hasClubMemberAccess
                  ? 'Your lessons and court bookings'
                  : hasLessonsAccess
                    ? 'Your upcoming lessons'
                    : 'Your court bookings'}
              </p>
            </div>
            <ScheduleViewHeaderToggle
              value={scheduleView}
              onChange={setScheduleView}
              variant="default"
              availableViews={['weekly', 'monthly']}
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : scheduleView === 'monthly' ? (
            <TermLessonCalendar
              occurrences={hasLessonsAccess ? lessonOccurrences : []}
              termStart={termStart}
              termEnd={termEnd}
              academyId={academy?.id}
              onReportAbsence={hasLessonsAccess ? handleReportAbsence : undefined}
              onRequestRefund={hasLessonsAccess ? handleRequestRefund : undefined}
              lessonsPath={paths?.lessons}
              extraEvents={hasCourtHire ? courtExtraEvents : []}
              onExtraEventClick={setSelectedCourtEvent}
            />
          ) : (
            <BookingWeeklySchedule
              occurrences={hasLessonsAccess ? lessonOccurrences : []}
              termStart={termStart}
              termEnd={termEnd}
              onOccurrenceClick={hasLessonsAccess ? handleOccurrenceClick : undefined}
              isMakeup={(occ) =>
                makeupOccurrenceSet.has(`${occ.bookingId}:${format(occ.date, 'yyyy-MM-dd')}`)
              }
              studentsById={studentsById}
              extraEvents={hasCourtHire ? courtExtraEvents : []}
              onExtraEventClick={setSelectedCourtEvent}
            />
          )}

          <OrgConsumerNoticesSection hasClubMemberAccess={hasClubMemberAccess} />
        </div>

        <OrgConsumerSidebar
          hasMemberships={hasMemberships}
          hasCourtHire={hasCourtHire}
          courtBookings={courtBookings}
          membershipData={membershipQuery.data}
          membershipLoading={membershipQuery.isLoading}
        />
      </div>

      {hasLessonsAccess ? (
        <>
          <LessonDetailModal
            occurrence={detailOccurrence}
            isOpen={!!detailOccurrence}
            onClose={() => setDetailOccurrence(null)}
            onReportAbsence={handleReportAbsence}
            onRequestRefund={handleRequestRefund}
            studentName={
              detailOccurrence ? studentsById[detailOccurrence.studentId] : undefined
            }
            creditsPath={paths?.credits}
            lessonsPath={paths?.lessons}
            reportSuccess={reportSuccess}
          />

          <RefundRequestModal
            isOpen={!!refundBooking}
            booking={refundBooking}
            lessonName={
              refundBooking
                ? lessonOccurrences.find((o) => o.bookingId === refundBooking.id)?.lessonName
                : undefined
            }
            defaultReason="Term lesson was cancelled. I would like to request a refund."
            onCancel={() => setRefundBooking(null)}
            onConfirm={handleRefundConfirm}
          />

          {absenceOccurrence ? (
            <ReportAbsenceFromCalendar
              occurrence={absenceOccurrence}
              isOpen={!!absenceOccurrence}
              onClose={() => setAbsenceOccurrence(null)}
              onSuccess={handleAbsenceSuccess}
            />
          ) : null}
        </>
      ) : null}

      {hasCourtHire ? (
        <CourtHireBookingDetailPopover
          event={selectedCourtEvent}
          isOpen={!!selectedCourtEvent}
          onClose={() => setSelectedCourtEvent(null)}
          bookingsListPath={courtBookingsPath}
        />
      ) : null}
    </div>
  );
}
